import React from 'react';
import {TextField, tokens, view} from '@sail/ui';
import type {
  Stripe as StripeJS,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeConstructorOptions,
  StripeElementStyle,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import type {MessageDescriptor} from '@sail/intl';
import {useIntl} from '@sail/intl';
import {useTokenValue} from '@sail/engine';
import type {IMerchantDataContext} from '../../context/merchant/MerchantDataContext';
import {useCustomFonts, useSoftAssertFailed, useTheming} from '../../context';
import * as utils from './utils';
import {messages} from './DebitCardForm';
import {useServiceEnvironment} from '../../context/environment/ServiceEnvironmentContext';

export type Props = {
  cardElement: StripeCardNumberElement | null;
  expiryElement: StripeCardExpiryElement | null;
  cardStatus: StripeCardNumberElementChangeEvent | null;
  expiryStatus: StripeCardExpiryElementChangeEvent | null;
  cvcStatus: StripeCardCvcElementChangeEvent | null;
  label?: MessageDescriptor<never>;
  loading: boolean;
  merchantData: IMerchantDataContext;
  overrideNumber: string | undefined;
  overrideExpiry: string | undefined;
  stripeJs?: StripeJS | null;
  clearOverrideFields: () => void;
  handleError: (error: string) => void;
  handleStripeJS: (stripe: StripeJS) => void;
  handleCardElement: (cardElement: StripeCardNumberElement) => void;
  handleExpiryElement: (expiryElement: StripeCardExpiryElement) => void;
  handleCardFieldStatus: (status: StripeCardNumberElementChangeEvent) => void;
  handleExpiryFieldStatus: (status: StripeCardExpiryElementChangeEvent) => void;
  handleCvcFieldStatus: (status: StripeCardCvcElementChangeEvent) => void;
  handleTokenLoading: (loading: boolean) => void;
  stripeJsV2Config?: utils.StripeJsV2Config;
  onFormLoaded?: () => void;
};

export const DebitCardFormIframe = ({
  cardElement,
  expiryElement,
  cardStatus,
  expiryStatus,
  cvcStatus,
  label,
  loading,
  merchantData,
  overrideNumber,
  overrideExpiry,
  stripeJs,
  clearOverrideFields,
  handleError,
  handleStripeJS,
  handleCardElement,
  handleExpiryElement,
  handleCardFieldStatus,
  handleExpiryFieldStatus,
  handleCvcFieldStatus,
  handleTokenLoading,
  stripeJsV2Config,
  onFormLoaded,
}: Props) => {
  const ref = React.useRef<HTMLElement | null>(null);
  const cardHTMLElement = React.useRef<HTMLElement | null>(null);
  const expiryHTMLElement = React.useRef<HTMLElement | null>(null);
  const cvcHTMLElement = React.useRef<HTMLElement | null>(null);
  const fonts = useCustomFonts();
  const intl = useIntl();
  const softAssertFailed = useSoftAssertFailed();
  const isMounted = React.useRef<boolean>(true);

  const theme = useTheming();
  const color = useTokenValue(tokens.color.text);
  const backgroundColor = useTokenValue(tokens.color.background.form);
  const fontSize = useTokenValue(tokens.font.body.small.size);
  const fontWeight = useTokenValue(tokens.font.body.small.weight);
  const lineHeight = useTokenValue(tokens.font.body.small.lineHeight);
  const invalidColor = useTokenValue(tokens.color.feedback.critical[500]);
  const serviceEnvironment = useServiceEnvironment();

  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    const loadStripeJSElements = async () => {
      const contentDocument = ref.current?.ownerDocument;
      const contentWindow = ref.current?.ownerDocument?.defaultView;

      if (!contentDocument || !contentWindow || !isMounted.current) {
        return;
      }

      const {stripeJsEndpoint} = serviceEnvironment;

      const stripe = await utils.loadStripeJSInWindow({
        locale: intl.locale as StripeConstructorOptions['locale'],
        contentWindow,
        merchantData: {
          merchantId: merchantData.merchantId,
          publishableKey: merchantData.publishableKey,
        },
        softAssertFailed,
        stripeJsEndpoint,
        stripeJsV2Config,
      });
      if (!stripe) {
        handleError(intl.formatMessage(messages.genericError));
        return;
      }

      if (isMounted.current) {
        handleStripeJS(stripe);
      }
    };

    loadStripeJSElements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      stripeJs &&
      cardHTMLElement.current &&
      expiryHTMLElement.current &&
      isMounted.current
    ) {
      const elements = stripeJs.elements({
        fonts,
      });

      const cardStyle: StripeElementStyle = {
        base: {
          color,
          backgroundColor,
          fontFamily: theme?.variables?.fontFamily,
          fontSize,
          fontWeight,
          lineHeight,
        },
        invalid: {
          color: invalidColor,
        },
      };

      const cardNumberStripeElement = elements.create('cardNumber', {
        style: cardStyle,
      });
      handleCardElement(cardNumberStripeElement);

      const cardExpiryStripeElement = elements.create('cardExpiry', {
        style: cardStyle,
      });
      handleExpiryElement(cardExpiryStripeElement);

      cardNumberStripeElement.mount(cardHTMLElement.current);
      cardExpiryStripeElement.mount(expiryHTMLElement.current);

      cardNumberStripeElement.on('change', handleCardFieldStatus);
      cardExpiryStripeElement.on('change', handleExpiryFieldStatus);

      if (stripeJsV2Config && cvcHTMLElement.current) {
        const cardCVCStripeElement = elements.create('cardCvc', {
          style: cardStyle,
        });

        cardCVCStripeElement.mount(cvcHTMLElement.current);
        cardCVCStripeElement.on('change', handleCvcFieldStatus);
      }

      onFormLoaded?.();

      handleTokenLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeJs]);

  return (
    <view.div
      css={{
        fontSize: 'body.small',
        color: 'text',
        stack: 'y',
        gap: 'medium',
      }}
      ref={ref}
    >
      <view.div
        css={{
          paddingY: 'xsmall',
          paddingX: 'space.0',
          stack: 'y',
          gap: 'small',
        }}
      >
        <view.span>
          {intl.formatMessage(label || messages.cardNumLabel)}
        </view.span>
        <view.div
          css={{
            stack: 'y',
            gap: 'xsmall',
          }}
        >
          <view.div
            css={{
              display: overrideNumber ? 'none' : 'block',
              backgroundColor: 'form',
              border: 'none',
              borderRadius: 'form',
              keyline: 'form',
              paddingY: 'xsmall',
              paddingX: 'small',
              width: 'fill',
            }}
            ref={cardHTMLElement}
          />
          {overrideNumber && (
            <TextField
              aria-label="card-number-input"
              disabled={loading}
              value={overrideNumber}
              data-testid="card-number-override-input"
              onChange={() => {
                clearOverrideFields();
                setTimeout(() => {
                  cardElement?.focus();
                });
              }}
            />
          )}
        </view.div>

        {cardStatus?.error?.message && (
          <view.span
            css={{
              fontSize: 'label.medium',
              color: 'feedback.critical',
            }}
          >
            {cardStatus.error.message}
          </view.span>
        )}
      </view.div>

      <view.div
        css={{
          stack: 'x',
          gap: 'small',
        }}
      >
        <view.div
          css={{
            paddingY: 'xsmall',
            paddingX: 'space.0',
            stack: 'y',
            gap: 'small',
          }}
        >
          <view.span>{intl.formatMessage(messages.cardExpDateLabel)}</view.span>
          <view.div
            css={{
              stack: 'y',
              gap: 'xsmall',
            }}
          >
            <view.div
              css={{
                display: overrideExpiry ? 'none' : 'block',
                backgroundColor: 'form',
                border: 'none',
                borderRadius: 'form',
                keyline: 'form',
                paddingY: 'xsmall',
                paddingX: 'small',
                width: 'fill',
              }}
              ref={expiryHTMLElement}
            />
            {overrideExpiry && (
              <TextField
                aria-label="card-expiry-input"
                disabled={loading}
                value={overrideExpiry}
                data-testid="card-expiry-override-input"
                onChange={() => {
                  clearOverrideFields();
                  setTimeout(() => {
                    expiryElement?.focus();
                  });
                }}
              />
            )}
          </view.div>
          {expiryStatus?.error?.message && (
            <view.span
              css={{
                fontSize: 'label.medium',
                color: 'feedback.critical',
              }}
            >
              {expiryStatus.error.message}
            </view.span>
          )}
        </view.div>

        {stripeJsV2Config ? (
          <view.div
            css={{
              paddingY: 'xsmall',
              paddingX: 'space.0',
              stack: 'y',
              gap: 'small',
            }}
          >
            <view.span>{intl.formatMessage(messages.cardCVCLabel)}</view.span>
            <view.div
              css={{
                display: 'block',
                backgroundColor: 'form',
                border: 'none',
                borderRadius: 'form',
                keyline: 'form',
                paddingY: 'xsmall',
                paddingX: 'small',
                width: 'fill',
              }}
              ref={cvcHTMLElement}
            />
            {cvcStatus?.error?.message && (
              <view.span
                css={{
                  fontSize: 'label.medium',
                  color: 'feedback.critical',
                }}
              >
                {cvcStatus.error.message}
              </view.span>
            )}
          </view.div>
        ) : null}
      </view.div>
    </view.div>
  );
};
